<template>
  <v-row no-gutters>
    <v-col>
      <v-row no-gutters class="pb-4">
        <v-col cols="8">
          <v-row no-gutters align="baseline">
            <v-col cols="auto" class="pr-4 font-weight-bold">
              {{ $i18n.t('admin.external_systems_table.connections') }}
            </v-col>
            <v-col cols="auto">
              <base-rounded-button
                icon="mdi-plus"
                size="x-small"
                color="success"
                :tooltip="$i18n.t('button.addConnection')"
                @click="openSystemEditAddDialog(undefined)"
              />
            </v-col>
            <v-spacer />
            <v-col cols="auto">
              <data-input
                :data-type="dataTypeText"
                prepend-inner-icon="mdi-magnify"
                :placeholder="$i18n.t('default.find')"
                clearable
                hide-details
                v-model="search"
                class="align-center justify-center"
              />
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <v-row no-gutters>
        <v-col cols="8" style="border: 1px solid rgba(0, 0, 0, 0.12);">
          <v-data-table
            dense
            :search="search"
            calculate-widths
            :footer-props="{
              itemsPerPageOptions: [10, 20, 50, 100],
            }"
            :items="computedExternalSystems"
            :headers="headers"
          >
            <template #header.system_id="{ header }">
              <v-tooltip bottom color="font">
                <template #activator="{ on }">
                  {{ header.text }}
                  <v-icon v-on="on" style="font-size: 16px" color="info">
                    mdi-help-circle
                  </v-icon>
                </template>
                {{ $i18n.t(`admin.external_systems_table.tooltips.system_id`) }}
              </v-tooltip>
            </template>
            <template #header.source_field="{ header }">
              <v-tooltip bottom color="font">
                <template #activator="{ on }">
                  {{ header.text }}
                  <v-icon v-on="on" style="font-size: 16px" color="info">
                    mdi-help-circle
                  </v-icon>
                </template>
                {{ $i18n.t(`admin.external_systems_table.tooltips.source_field`) }}
              </v-tooltip>
            </template>
            <template #[`header.${attributeIdName}`]="{ header }">
              <v-tooltip bottom color="font">
                <template #activator="{ on }">
                  {{ header.text }}
                  <v-icon v-on="on" style="font-size: 16px" color="info">
                    mdi-help-circle
                  </v-icon>
                </template>
                {{ $i18n.t(`admin.external_systems_table.tooltips.${attributeIdName}`) }}
              </v-tooltip>
            </template>
            <template #item.actions="{item}">
              <dots-menu
                :items="dotsItems"
                @edit="openSystemEditAddDialog(item)"
                @delete="openDeleteDialog(item)"
                @refreshView="handleConnectionRefresh(item.id)"
                :disabled="dotsMenuDisabled"
              ></dots-menu>
            </template>
            <template #item.datasource="{item}">
              {{ getDataSourceByName(item) }}
            </template>
            <template #[`item.${attributeIdName}`]="{item}">
              {{ getDataSourceRelationByName(item) }}
            </template>
            <template #item.datasourceOrTableType="{item}">
              {{ item.relation_type === 'table' ? $i18n.t('dialog.tableView') : $i18n.t('dialog.dataSource') }}
            </template>
            <template #item.datasourceOrTable="{item}">
              {{
                item.relation_type === 'table'
                  ? `${item.gisbox_schema_name}.${item.gisbox_table_name}`
                  : getDataSourceByName(item)
              }}
            </template>
            <template #item.datasourceOrTableField="{item}">
              {{ item.relation_type === 'table' ? item.relation_field : getDataSourceRelationByName(item) }}
            </template>
          </v-data-table>
        </v-col>
      </v-row>
      <main-input-dialog
        v-if="isSystemDialogLoaded"
        :is-visible.sync="isSystemDialogVisible"
        :inputs="dialogInputs"
        :agree-text="agreeText"
        dialog-width="40%"
        :title-translation="systemDialogName"
        @modalClosed="onClose"
        :loading="isConfirmButtonLoading"
        @agree="computedAgreeAction"
      >
        <template v-for="(input, idx) of dialogInputs.filter(dI => !dI.custom)" #[`${input.name}.input`]="slot">
          <v-row no-gutters :key="idx">
            <v-col cols="9">
              <data-input v-model="payload[input.name]" v-bind="slot" @input="onInput(slot.name, $event)" />
            </v-col>
          </v-row>
        </template>
        <template #relation_type.input="slot">
          <v-row class="mb-4">
            <v-col cols="12">
              <v-radio-group
                class="my-0 py-0 input--type"
                v-model="payload['relation_type']"
                @change="payload['read_only'] = payload['read_only'] || false"
                v-bind="slot"
              >
                <v-radio :label="$i18n.t('dialog.dataSource')" value="datasource"></v-radio>
                <v-radio :label="$i18n.t('dialog.tableView')" value="table"></v-radio>
              </v-radio-group>
            </v-col>
          </v-row>
        </template>
        <template #read_only.input="slot">
          <v-row class="mb-4">
            <v-col cols="12">
              <v-checkbox v-bind="slot" v-model="payload['read_only']">
                <template #label>
                  <div class="text-body-2 text-left label-dark">
                    {{ slot.label }}
                  </div>
                </template>
              </v-checkbox>
            </v-col>
          </v-row>
        </template>
        <template #system.input="slot">
          <v-row no-gutters>
            <v-col cols="9">
              <data-input v-model="payload['system']" v-bind="slot" @input="onInput(slot.name, $event)" />
            </v-col>
          </v-row>
        </template>
      </main-input-dialog>
      <main-dialog
        v-if="isDeleteDialogLoaded"
        :is-visible.sync="isDeleteDialogVisible"
        :title="$i18n.t('dialog.title.deleteConnection')"
        :body="$i18n.t('dialog.body.deleteConnectionConfirmation')"
        @agree="handleConnectionDelete"
        :loading="isConfirmButtonLoading"
        agree-text="dialog.agreeDelete"
        @modalClosed="onClose"
      />
    </v-col>
  </v-row>
</template>

<script>
import BaseRoundedButton from '@/components/BaseRoundedButton';
import DataInput from '@/components/DataInput';
import { get } from 'vuex-pathify';
import DotsMenu from '@/components/DotsMenu';

export default {
  name: 'ExternalSystemsTable',
  components: {
    MainDialog: () => import('@/components/MainDialog'),
    MainInputDialog: () => import('@/components/MainInputDialog'),
    DotsMenu,
    DataInput,
    BaseRoundedButton,
  },
  props: {
    attributeIdName: {
      type: String,
      default: 'attribute_id',
    },
    inputs: {
      type: Array,
      default: () => {
        return [];
      },
    },
    externalSystems: {
      type: Array,
      default: () => {
        return [];
      },
    },
    availableSystems: {
      type: Array,
      default: () => {
        return [];
      },
    },
    initGets: {
      type: Function,
      required: true,
    },
    deleteConnection: {
      type: Function,
      required: true,
    },
    editConnection: {
      type: Function,
      required: true,
    },
    addConnection: {
      type: Function,
      required: true,
    },
    refreshView: {
      type: Function,
    },
  },
  data: vm => ({
    search: '',
    typeInputs: {
      datasource: ['datasource', vm.attributeIdName, 'join_type', 'migration_type', 'sql_columns', 'read_only'],
      table: [
        'gisbox_schema_name',
        'gisbox_table_name',
        'relation_field',
        'join_type',
        'migration_type',
        'sql_columns',
        'read_only',
      ],
    },
    isSystemDialogVisible: false,
    isSystemDialogLoaded: false,
    isDeleteDialogVisible: false,
    isDeleteDialogLoaded: false,
    isConfirmButtonLoading: false,
    dataTypeText: {
      name: 'text',
    },
    dotsMenuDisabled: false,
    editing: false,
    payload: {},
  }),
  computed: {
    dataSources: get('layers/metadata'),
    dotsItems() {
      return [{ name: 'edit' }, ...(this.refreshView ? [{ name: 'refreshView' }] : []), { name: 'delete' }];
    },
    selectedDatasourceAttributes() {
      return this.payload?.datasource ? this.selectedDatasource.attributes_schema.attributes : undefined;
    },
    selectedDatasource() {
      return this.dataSources[this.payload?.datasource || ''];
    },
    agreeText() {
      return this.editing ? 'dialog.agreeSave' : 'dialog.agreeAdd';
    },
    systemDialogName() {
      return this.editing ? this.$i18n.t('dialog.title.editConnection') : this.$i18n.t('dialog.title.addConnection');
    },
    computedAgreeAction() {
      return this.editing ? this.handleConnectionEdit : this.handleConnectionAdd;
    },
    computedExternalSystems() {
      return this.externalSystems;
    },
    computedHeadersInputs() {
      const inputs = JSON.parse(JSON.stringify(this.inputs));
      const index = inputs.indexOf('relation_type');
      if (index > -1) {
        inputs[index] = 'datasourceOrTableType';
        inputs.splice(index + 1, 0, 'datasourceOrTable');
        inputs.splice(index + 2, 0, 'datasourceOrTableField');
      }
      return inputs;
    },
    headers() {
      return [
        ...this.computedHeadersInputs
          .filter(i => i !== 'password')
          .map(value => {
            return {
              text: this.$i18n.t(`admin.external_systems_table.headers.${value}`),
              value: value,
            };
          }),
        {
          text: this.$i18n.t('admin.zdmStreetLights.tableHeaders.options'),
          value: 'actions',
          sortable: false,
          align: 'center',
          width: '50px',
        },
      ];
    },
    sortedDataSources() {
      const sources = this.$_objectToArray(this.dataSources).filter(
        dataSource => dataSource.scope !== 'module' && dataSource.scope !== 'dxf' && dataSource.scope !== null
      );
      return this.$_sortObjectsByAttribute([...sources], 'verbose_name');
    },
    typeValue() {
      return this.payload['relation_type'] || '';
    },
    computedInputs() {
      if (this.inputs.includes('relation_type')) {
        return [...this.inputs, ...(this.typeInputs[this.typeValue] || [])];
      } else {
        return this.inputs;
      }
    },
    dialogInputs() {
      return this.computedInputs.map(input => {
        return {
          name: input,
          straightTitle: this.$i18n.t(`admin.external_systems_table.headers.${input}`),
          dataType: this.getDataType(input),
          items: this.getItems(input),
          clearable: true,
          autocomplete: 'off',
          cols: '9',
          ...(input === 'password' && { type: 'password', autocomplete: 'new-password' }),
          ...((input === 'datasource' || input === this.attributeIdName) && {
            itemValue: this.getItemValue(input),
            itemText: 'verbose_name',
          }),
          ...(input === 'read_only' && {
            leftCol: 0,
            label: this.$i18n.t(`admin.external_systems_table.headers.${input}`),
            custom: true,
          }),
          ...(input === 'relation_type' && { dense: true, row: true, hideDetails: true, leftCol: 0, custom: true }),
          ...(!['sql_columns', 'read_only'].includes(input) && { rules: [v => !!v || v == '0' || 'rules.required'] }),
          ...(input === 'schema_name' && this.payload.system === 'MySQL' ? { disabled: true } : {}),
        };
      });
    },
  },
  methods: {
    getDataType(value) {
      if (
        value === 'system' ||
        value === 'datasource' ||
        value === this.attributeIdName ||
        value === 'join_type' ||
        value === 'migration_type'
      ) {
        return {
          name: 'select',
        };
      } else if (value === 'port') {
        return {
          name: 'integer',
        };
      } else if (value === 'sql_columns') {
        return {
          name: 'textarea',
        };
      } else {
        return {
          name: 'text',
        };
      }
    },
    getDataSourceByName(value) {
      const result = this.$_objectToArray(this.dataSources).find(d => d.name === value.datasource);
      return result?.verbose_name || '';
    },
    getDataSourceRelationByName(value) {
      const { datasource } = value;
      const attributeId = value[this.attributeIdName];
      const r = this.dataSources[datasource].attributes_schema.attributes.find(a => a.id === attributeId);
      return r ? r.verbose_name : attributeId;
    },
    getItems(value) {
      if (value === 'system') {
        return this.availableSystems;
      } else if (value === 'datasource') {
        return this.sortedDataSources;
      } else if (value === this.attributeIdName) {
        return this.selectedDatasourceAttributes;
      } else if (value === 'join_type') {
        return [
          { text: 'Inner', value: 'inner' },
          { text: 'Right', value: 'right' },
          { text: 'Left', value: 'left' },
          { text: 'Full', value: 'full' },
        ];
      } else if (value === 'migration_type') {
        return [
          { text: this.$i18n.t(`admin.external_systems_table.overwrite`), value: 'overwrite' },
          { text: this.$i18n.t(`admin.external_systems_table.update`), value: 'update' },
        ];
      } else {
        return [];
      }
    },
    getItemValue(value) {
      if (value === 'datasource') {
        return 'name';
      } else {
        return 'id';
      }
    },
    async handleConnectionRefresh(id) {
      this.dotsMenuDisabled = true;
      try {
        await this.refreshView(id);
        this.dotsMenuDisabled = false;
      } catch {
        this.dotsMenuDisabled = false;
      }
    },
    async handleConnectionDelete() {
      this.isConfirmButtonLoading = true;
      try {
        await this.deleteConnection(this.payload.id);
        this.isDeleteDialogVisible = false;
      } finally {
        await this.onClose();
      }
    },
    async handleConnectionAdd() {
      this.isConfirmButtonLoading = true;
      const body = JSON.parse(JSON.stringify(this.payload));
      const { relation_type: relationType } = body;
      if (relationType) {
        const relationTypeInputs = this.typeInputs[relationType];
        const relationTypes = Object.keys(this.typeInputs);
        relationTypes
          .filter(rT => rT !== relationType)
          .forEach(rT => {
            this.typeInputs[rT].forEach(input => {
              if (!relationTypeInputs.includes(input)) {
                delete body[input];
              }
            });
          });
      }
      this.isConfirmButtonLoading = false;
      try {
        await this.addConnection(body);
        this.isSystemDialogVisible = false;
        this.isSystemDialogLoaded = false;
        this.onClose();
      } catch {
        this.isConfirmButtonLoading = false;
      }
    },
    async handleConnectionEdit() {
      this.isConfirmButtonLoading = true;
      const id = this.payload.id;
      const body = JSON.parse(JSON.stringify(this.payload));
      const { relation_type: relationType } = body;
      if (relationType) {
        const relationTypeInputs = this.typeInputs[relationType];
        const relationTypes = Object.keys(this.typeInputs);
        relationTypes
          .filter(rT => rT !== relationType)
          .forEach(rT => {
            this.typeInputs[rT].forEach(input => {
              if (!relationTypeInputs.includes(input)) {
                delete body[input];
              }
            });
          });
      }
      delete body.id;
      delete body.status_description;
      delete body.status;
      delete body.last_update;
      this.isConfirmButtonLoading = false;
      try {
        await this.editConnection({ body, id });
        this.isSystemDialogVisible = false;
        this.isSystemDialogLoaded = false;
        this.onClose();
      } catch {
        this.isConfirmButtonLoading = false;
      }
    },
    onClose() {
      this.payload = {};
      this.isConfirmButtonLoading = false;
    },
    openSystemEditAddDialog(value) {
      this.editing = value ? true : false;
      this.payload = { ...value };
      this.isSystemDialogVisible = true;
      this.isSystemDialogLoaded = true;
    },
    openDeleteDialog(value) {
      this.payload = { ...value };
      this.isDeleteDialogLoaded = true;
      this.isDeleteDialogVisible = true;
    },
    async init() {
      await this.initGets();
    },
    onInput(name, value) {
      if (name === 'system' && value === 'MySQL') {
        this.payload['schema_name'] = this.payload['dbname'] || '';
      } else if (name === 'dbname' && this.payload.system === 'MySQL') {
        this.$set(this.payload, 'schema_name', value);
        this.$forceUpdate();
      }
    },
  },
  mounted() {
    this.init();
  },
};
</script>

<style scoped lang="scss">
.label-dark {
  color: #202124;
}
::v-deep {
  table > tbody > tr > td:last-child,
  table > thead > tr > th:last-child {
    position: sticky !important;
    position: -webkit-sticky !important;
    right: 0;
    z-index: 2;
    background: white;
    border-left: thin solid rgba(0, 0, 0, 0.12);
  }
  .v-data-table-header th {
    white-space: nowrap;
  }
  .input--type {
    .v-label {
      font-size: 14px;
    }
  }
}
</style>
